import { useCallback, useEffect, useState } from 'react';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { Modal, FormField } from '../components/modal';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';
import { EmptyState } from '@/components/ui/empty-state';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { ChalkboardTeacher, Plus, EnvelopeSimple, PencilSimple, Archive, Play, ArrowClockwise } from '@phosphor-icons/react';
import { getMySchoolId, listTeachers, addTeacher, updateTeacher, deleteTeacher, resendTeacherInvite, type SchoolTeacher } from '../lib/direction';

const listShellClass = "bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] overflow-hidden divide-y divide-[var(--naia-line)]";
const rowStaticClass = "flex items-center gap-[10px] px-[14px] py-3";

function accountStatus(t: SchoolTeacher): string | null {
  if (!t.email) return 'Pas de compte (email manquant)';
  if (t.invite_status === 'invited') return 'Invitation en attente (choix du mot de passe)';
  if (t.user_id || t.invite_status === 'assigned') return 'Compte actif';
  return 'Invitation en attente';
}

export default function DirectionTeachers() {
  const [schoolId, setSchoolId] = useState<string | null>(null);
  const [teachers, setTeachers] = useState<SchoolTeacher[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const [editing, setEditing] = useState<SchoolTeacher | 'new' | null>(null);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const sid = await getMySchoolId();
      setSchoolId(sid);
      if (sid) setTeachers(await listTeachers(sid));
    } catch {
      setTeachers([]);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  /* Une resynchronisation qui echoue ne doit pas vider la liste : un ecran vide
     apres un ajout reussi affirmerait que l'enseignant n'existe pas. */
  const refreshTeachers = useCallback(async () => {
    if (!schoolId) return;
    try {
      setTeachers(await listTeachers(schoolId));
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    }
  }, [schoolId]);

  const openNew = () => { setEditing('new'); setFirst(''); setLast(''); setEmail(''); };
  const openEdit = (t: SchoolTeacher) => { setEditing(t); setFirst(t.first_name); setLast(t.last_name); setEmail(t.email ?? ''); };

  const submit = async () => {
    if (!schoolId || saving || (!first.trim() && !last.trim())) return;
    if (editing === 'new' && !email.trim()) {
      window.alert("L'email est requis pour envoyer l'invitation à l'enseignant.");
      return;
    }
    setSaving(true);
    setNotice(null);
    try {
      if (editing === 'new') {
        const t = await addTeacher({ schoolId, firstName: first, lastName: last, email });
        setTeachers((p) => [...p, t]);
        if (t.invite_status === 'invited') {
          setNotice(`Invitation envoyée à ${t.email}.`);
        } else if (t.invite_status === 'assigned') {
          setNotice(`Compte existant lié pour ${t.email}.`);
        }
      } else if (editing) {
        const hadEmail = Boolean(editing.email);
        const hadUser = Boolean(editing.user_id);
        await updateTeacher(editing.id, { first_name: first.trim(), last_name: last.trim(), email: email.trim() || null });
        const nextEmail = email.trim() || null;
        setTeachers((p) => p.map((x) => (x.id === editing.id
          ? { ...x, first_name: first.trim(), last_name: last.trim(), email: nextEmail, user_id: hadUser ? x.user_id : (nextEmail && !hadEmail ? null : x.user_id) }
          : x)));
        if (nextEmail && !hadUser && (!hadEmail || nextEmail !== editing.email)) {
          setNotice(`Invitation envoyée à ${nextEmail}.`);
        }
        await refreshTeachers();
      }
      setEditing(null);
    } finally { setSaving(false); }
  };

  const onReactivate = async (t: SchoolTeacher) => { await updateTeacher(t.id, { active: true }); setTeachers((p) => p.map((x) => (x.id === t.id ? { ...x, active: true } : x))); };
  const onArchive = async (t: SchoolTeacher) => {
    if (!window.confirm(`Archiver ${t.first_name} ${t.last_name} ?`)) return;
    await deleteTeacher(t.id);
    setTeachers((p) => p.map((x) => (x.id === t.id ? { ...x, active: false } : x)));
  };

  const visibleTeachers = showArchived ? teachers : teachers.filter((t) => t.active);

  const canResendInvite = (t: SchoolTeacher) => Boolean(t.active && t.email && t.invite_status !== 'assigned');

  const onResendInvite = async (t: SchoolTeacher) => {
    if (!canResendInvite(t) || resendingId) return;
    setResendingId(t.id);
    setNotice(null);
    try {
      const status = await resendTeacherInvite(t.id);
      if (status === 'assigned') {
        setNotice(`Compte existant lié pour ${t.email}.`);
        await refreshTeachers();
      } else {
        setNotice(`Invitation renvoyée à ${t.email}.`);
      }
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'Échec du renvoi.');
    } finally {
      setResendingId(null);
    }
  };

  return (
    <>
      <HeaderContent
        width="md"
        showBurger
        eyebrow="Mode direction"
        eyebrowIcon={<ChalkboardTeacher size={11} weight="bold" />}
        actions={!loadFailed && <Button onClick={openNew}><Plus size={16} /> Ajouter un enseignant</Button>}
      >
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Enseignants</h1>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Ajoutez un enseignant avec son email : un compte est créé et un lien de connexion lui est envoyé.
          </p>
        </div>
      </HeaderContent>
      <main className="max-w-[880px] mx-auto px-5 pb-16 pt-4">

        {notice && (
          <p className="mb-4 px-[14px] py-[10px] rounded-[10px] bg-primary/10 text-primary text-sm font-semibold">
            {notice}
          </p>
        )}

        {loading ? (
          <p className="text-[var(--naia-text-secondary)] py-4">Chargement…</p>
        ) : loadFailed ? (
          <div className="flex flex-col items-start gap-3">
            <Alert variant="destructive">
              La liste des enseignants n'a pas pu être chargée. Elle est intacte.
            </Alert>
            <Button variant="outline" size="sm" className="min-h-[44px]" onClick={() => void load()}>
              <ArrowClockwise size={15} weight="bold" />
              Réessayer
            </Button>
          </div>
        ) : !schoolId ? (
          <EmptyState icon={ChalkboardTeacher} title="Aucun établissement rattaché" description="Votre compte n'est lié à aucun établissement pour le moment." />
        ) : (
          <>
            <label className="flex items-center gap-2 mb-3 min-h-11 cursor-pointer text-[12px] font-semibold text-[var(--naia-text-secondary)]">
              <Checkbox checked={showArchived} onCheckedChange={(v) => setShowArchived(v === true)} />
              Afficher les enseignants archivés
            </label>
            {visibleTeachers.length === 0 ? (
              teachers.length === 0 ? (
                <EmptyState icon={ChalkboardTeacher} title="Aucun enseignant pour l'instant" description="Ajoutez un enseignant avec son email : il recevra un lien de connexion." actionLabel="Ajouter un enseignant" onAction={openNew} />
              ) : (
                <EmptyState icon={Archive} title="Aucun enseignant archivé" description="Les enseignants que vous archivez apparaîtront ici." />
              )
            ) : (
              <div className={listShellClass}>
                {visibleTeachers.map((t) => (
                  <div key={t.id} className={rowStaticClass}>
                    <span className={cn("shrink-0", t.active ? "text-primary" : "text-[var(--naia-text-secondary)] opacity-50")}>
                      <ChalkboardTeacher size={20} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className={cn("font-bold text-[14.5px] text-[var(--naia-text-primary)]", !t.active && "opacity-[0.55]")}>{t.first_name} {t.last_name}{!t.active ? ' · archivé' : ''}</div>
                      {t.email && <div className="text-[12px] text-[var(--naia-text-secondary)]">{t.email}</div>}
                      <div className="text-[11px] text-[var(--naia-text-secondary)] mt-0.5">{accountStatus(t)}</div>
                    </div>
                    {canResendInvite(t) && (
                      <Button variant="ghost" size="icon" className="shrink-0" onClick={() => onResendInvite(t)} disabled={resendingId === t.id} title="Renvoyer l'invitation" aria-label="Renvoyer l'invitation">
                        {resendingId === t.id ? '…' : <EnvelopeSimple size={15} />}
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="shrink-0" onClick={() => openEdit(t)} title="Modifier" aria-label="Modifier"><PencilSimple size={15} /></Button>
                    {t.active ? (
                      <Button variant="ghost" size="icon" className="shrink-0" onClick={() => onArchive(t)} title="Archiver" aria-label="Archiver"><Archive size={15} /></Button>
                    ) : (
                      <Button variant="ghost" size="icon" className="shrink-0" onClick={() => onReactivate(t)} title="Réactiver" aria-label="Réactiver"><Play size={15} /></Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      <Modal
        open={editing !== null}
        title={editing === 'new' ? 'Ajouter un enseignant' : "Modifier l'enseignant"}
        onClose={() => setEditing(null)}
        footer={<>
          <Button variant="secondary" onClick={() => setEditing(null)}>Annuler</Button>
          <Button onClick={submit} disabled={saving || (!first.trim() && !last.trim()) || (editing === 'new' && !email.trim())}>{saving ? 'Enregistrement…' : 'Enregistrer'}</Button>
        </>}
      >
        <FormField label="Prénom" value={first} onChange={setFirst} placeholder="Prénom" autoFocus />
        <FormField label="Nom" value={last} onChange={setLast} placeholder="Nom" />
        <FormField
          label={editing === 'new' ? 'Email' : 'Email (optionnel)'}
          value={email}
          onChange={setEmail}
          placeholder="email@etablissement.fr"
          type="email"
          onEnter={submit}
        />
        {editing === 'new' && (
          <p className="text-[12px] text-[var(--naia-text-secondary)] -mt-1">
            Un lien d'invitation sera envoyé à cette adresse.
          </p>
        )}
      </Modal>
    </>
  );
}
